import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { removeLoadingKey } from '../helpers/common';

export const ReportEmailSettingsLoadingKeys = {
  getSettings: 'REPORT_EMAIL_SETTINGS_GET_SETTINGS',
  updateSettings: 'REPORT_EMAIL_SETTINGS_UPDATE_SETTINGS',
  sendPreview: 'REPORT_EMAIL_SETTINGS_SEND_PREVIEW',
  setNotifications: 'REPORT_EMAIL_SETTINGS_SET_NOTIFICATIONS',
};

const initialState = {
  settings: {},
  loading: false,
  loadingKeys: [],
  error: null,
};

// Map report types to their metadata API endpoints
const reportTypeToMetadataEndpoint = {
  'received-inventory': 'received-inventory-email',
  'received-shipments': 'received-shipments-email',
  'warehouse-inventory-value': 'warehouse-inventory-email',
  'inventory-cost-change': 'inventory-cost-change-email',
  'shipping-each-cost-change': 'shipping-each-cost-change-email',
  'missing-inventory-cost': 'missing-inventory-cost-email',
  'eadin-cogs': 'eadin-cogs-email',
  'low-stock-alerts': 'low-stock-alerts-email',
  'amazon-fba-low-stock-alerts': 'amazon-fba-low-stock-alerts-email',
  'low-stock-sold-out-alerts': 'low-stock-sold-out-alerts-email',
  'bom-packaging': 'bom-packaging-email',
  'source-skus-by-product': 'source-skus-by-product-email',
  'amazon-month-end-close-summary': 'amazon-month-end-close-summary-email',
  'inbound-shipments': 'inbound-shipments-email',
  'monthly-ap-po-paid': 'monthly-ap-po-paid-email',
  'billcom-po-reconciliation': 'billcom-po-reconciliation-email',
  'billcom-ap-writeback-status': 'billcom-ap-writeback-status-email',
  'billcom-bank-cash-recon': 'billcom-bank-cash-recon-email',
  'eadin-catalog': 'eadin-catalog-email',
  'eadin-inventory': 'eadin-inventory-email',
  'monthly-po-ap-due': 'monthly-po-ap-due-email',
  'monthly-po-ap-progress': 'monthly-po-ap-progress-email',
  'sales-by-product': 'sales-by-product-email',
  'sales-by-product-by-day': 'sales-by-product-by-day-email',
  'sales-revenue': 'sales-revenue-email',
  'sales-revenue-monthly': 'sales-revenue-monthly-email',
  'shopify-profit-summary': 'shopify-profit-summary-email',
  'shopify-profit-by-sku-summary': 'shopify-profit-by-sku-summary-email',
  'total-product-revenue': 'total-product-revenue-email',
};

// Map report types to their preview email API endpoints
const reportTypeToPreviewEndpoint = {
  'received-inventory': 'received-inventory/preview-email',
  'received-shipments': 'received-shipments/preview-email',
  'warehouse-inventory-value': 'warehouse-inventory-value/preview-email',
  'inventory-cost-change': 'inventory-cost-change/preview-email',
  'shipping-each-cost-change': 'shipping-each-cost-change/preview-email',
  'missing-inventory-cost': 'missing-inventory-cost/send-preview',
  'eadin-cogs': 'eadin-cogs/preview-email',
  'low-stock-alerts': 'low-stock-alerts/preview-email',
  'amazon-fba-low-stock-alerts': 'amazon-fba-low-stock-alerts/preview-email',
  'low-stock-sold-out-alerts': 'low-stock-sold-out-alerts/preview-email',
  'bom-packaging': 'bom-packaging/preview-email',
  'source-skus-by-product': 'source-skus-by-product/preview-email',
  'amazon-month-end-close-summary': 'amazon-month-end-close-summary/preview-email',
  'inbound-shipments': 'inbound-shipments/preview-email',
  'monthly-ap-po-paid': 'monthly-ap-po-paid/preview-email',
  'billcom-po-reconciliation': 'billcom-po-reconciliation/preview-email',
  'billcom-ap-writeback-status': 'billcom-ap-writeback-status/preview-email',
  'billcom-bank-cash-recon': 'billcom-bank-cash-recon/preview-email',
  'eadin-catalog': 'eadin-catalog/preview-email',
  'eadin-inventory': 'eadin-inventory/preview-email',
  'monthly-po-ap-due': 'monthly-po-ap-due/preview-email',
  'monthly-po-ap-progress': 'monthly-po-ap-progress/preview-email',
  'sales-by-product': 'sales-by-product/preview-email',
  'sales-by-product-by-day': 'sales-by-product-by-day/preview-email',
  'sales-revenue': 'sales-revenue/preview-email',
  'sales-revenue-monthly': 'sales-revenue-monthly/preview-email',
  'shopify-profit-summary': 'shopify-profit-summary/preview-email',
  'shopify-profit-by-sku-summary': 'shopify-profit-by-sku-summary/preview-email',
  'total-product-revenue': 'total-product-revenue/preview-email',
};

export const getReportEmailSettings = createAsyncThunk(
  'reportEmailSettings/getSettings',
  async (reportType, { rejectWithValue }) => {
    try {
      const endpoint = reportTypeToMetadataEndpoint[reportType];
      if (!endpoint) {
        return rejectWithValue({ reportType, error: `Invalid report type: ${reportType}` });
      }
      const { data } = await axios.get(`/api/v1/metadata/${endpoint}`);
      return { reportType, data };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue({ reportType, error: error.response.data.error || error.response.data });
      }
      return rejectWithValue({ reportType, error: 'Failed to load settings' });
    }
  }
);

export const updateReportEmailSettings = createAsyncThunk(
  'reportEmailSettings/updateSettings',
  async ({ reportType, ...payload }, { rejectWithValue }) => {
    try {
      const endpoint = reportTypeToMetadataEndpoint[reportType];
      if (!endpoint) {
        return rejectWithValue({ reportType, error: `Invalid report type: ${reportType}` });
      }
      const { data } = await axios.post(`/api/v1/metadata/${endpoint}`, payload);
      return { reportType, data };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue({ reportType, error: error.response.data.error || error.response.data });
      }
      return rejectWithValue({ reportType, error: 'Failed to update settings' });
    }
  }
);

export const sendReportPreviewEmail = createAsyncThunk(
  'reportEmailSettings/sendPreview',
  async ({ reportType, ...payload }, { rejectWithValue }) => {
    try {
      const endpoint = reportTypeToPreviewEndpoint[reportType];
      if (!endpoint) {
        return rejectWithValue({ reportType, error: `Invalid report type: ${reportType}` });
      }
      const { data } = await axios.post(`/api/v1/reports/${endpoint}`, payload);
      return { reportType, data };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue({ reportType, error: error.response.data.error || error.response.data });
      }
      return rejectWithValue({ reportType, error: 'Failed to send preview email' });
    }
  }
);

export const setReportNotificationsEnabled = createAsyncThunk(
  'reportEmailSettings/setNotificationsEnabled',
  async ({ reportType, enabled }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/metadata/report-notifications', { reportType, enabled });
      return { reportType, data };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue({ reportType, error: error.response.data.error || error.response.data });
      }
      return rejectWithValue({ reportType, error: 'Failed to update notifications setting' });
    }
  }
);

export const reportEmailSettings = createSlice({
  name: 'reportEmailSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getReportEmailSettings.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ReportEmailSettingsLoadingKeys.getSettings];
        state.error = null;
      })
      .addCase(getReportEmailSettings.fulfilled, (state, action) => {
        state.loading = false;
        const { reportType, data } = action.payload;
        state.settings[reportType] = data?.settings || {};
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.getSettings, state.loadingKeys);
        state.error = null;
      })
      .addCase(getReportEmailSettings.rejected, (state, action) => {
        state.loading = false;
        const { reportType, error } = action.payload || {};
        if (reportType) {
          state.settings[reportType] = null;
        }
        state.error = error || action.error?.message || 'Failed to load settings';
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.getSettings, state.loadingKeys);
      })
      // UPDATE
      .addCase(updateReportEmailSettings.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ReportEmailSettingsLoadingKeys.updateSettings];
        state.error = null;
      })
      .addCase(updateReportEmailSettings.fulfilled, (state, action) => {
        state.loading = false;
        const { reportType, data } = action.payload;
        const ret = data || {};
        state.settings[reportType] = ret.settings ?? state.settings[reportType] ?? {};
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.updateSettings, state.loadingKeys);
        state.error = null;
      })
      .addCase(updateReportEmailSettings.rejected, (state, action) => {
        state.loading = false;
        const { reportType, error } = action.payload || {};
        if (reportType) {
          state.settings[reportType] = null;
        }
        state.error = error || action.error?.message || 'Failed to update settings';
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.updateSettings, state.loadingKeys);
      })
      // SEND PREVIEW
      .addCase(sendReportPreviewEmail.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ReportEmailSettingsLoadingKeys.sendPreview];
        state.error = null;
      })
      .addCase(sendReportPreviewEmail.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.sendPreview, state.loadingKeys);
        state.error = null;
      })
      .addCase(sendReportPreviewEmail.rejected, (state, action) => {
        state.loading = false;
        const { error } = action.payload || {};
        state.error = error || action.error?.message || 'Failed to send preview';
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.sendPreview, state.loadingKeys);
      })
      // NOTIFICATIONS TOGGLE
      .addCase(setReportNotificationsEnabled.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, ReportEmailSettingsLoadingKeys.setNotifications];
        state.error = null;
      })
      .addCase(setReportNotificationsEnabled.fulfilled, (state, action) => {
        const { reportType, data } = action.payload;
        state.settings[reportType] = {
          ...(state.settings[reportType] || {}),
          notificationsEnabled: data?.notificationsEnabled,
        };
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.setNotifications, state.loadingKeys);
        state.error = null;
      })
      .addCase(setReportNotificationsEnabled.rejected, (state, action) => {
        const { error } = action.payload || {};
        state.error = error || action.error?.message || 'Failed to update notifications setting';
        state.loadingKeys = removeLoadingKey(ReportEmailSettingsLoadingKeys.setNotifications, state.loadingKeys);
      });
  },
});

export default reportEmailSettings.reducer;
